/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

export interface Edits<T> {
  oldData?: T;
  newData?: T;
  created?: boolean;
  deleted?: boolean;
}
export enum LoadStatus {
  NONE = 0,
  IN_PROGRESS = 1,
  DONE = 2,
  FAILED = 3,
}
export interface LoadingState {
  status: LoadStatus;
  error?: string;
  startedAt?: string;
  endedAt?: string;
  failedAt?: string;
}
export interface DatabaseItem {
  id: string;
  name: string;
  desc?: string;
  hidden?: boolean;
  deleted: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** APP CONFIG */

export interface AppConfig {
  googleClientId: string;
  name: string;
  logoIcon: string;
  logoLargeIcon: string;
  appVersions: AppVersion[];
  defaultEnv: string;
  defaultChar: string;
  ttsDefault: boolean;
  disableSOS: boolean;
  disableDevTools: boolean;
  presurveyUrl: string;
  postsurveyUrl: string;
  defaultGoal: string;
  defaultTopic: string;
  defaultCohort: string;
  defaultIntro: string;
  disabledGoals: string[];
  disabledTopics: string[];
  enabledGoals: string[];
  enabledTopics: string[];
  priorityGoals: string[];
}
export interface AppVersion {
  appId: string;
  appUpdateUrl: string;
  platform: string;
  versionMin: string;
  versionLatest: string;
}

/** DIALOGUE MESSAGE */

export enum AnimationType {
  None = "none",
  Greeting = "greeting",
  Farewell = "farewell",
  Talk = "talk",
  Poke = "poke",
  Idle = "idle",
  Celebration = "celebration",
  CelebrationL = "celebrationL",
}
export enum DialogueMessageType {
  None = "None",
  Signup = "Signup",
  Login = "Login",
  LoginShort = "LoginShort",
  LoginLong = "LoginLong",
  LogoutPositive = "LogoutPositive",
  LogoutNegative = "LogoutNegative",
  Poke = "Poke",
  LessonPoor = "LessonPoor",
  LessonGood = "LessonGood",
  TopicSet = "TopicSet",
  TopicRecommended = "TopicRecommended",
  TopicMasteryGain = "TopicMasteryGain",
  TopicMastered = "TopicMastered",
  GoalSet = "GoalSet",
  GoalRecommended = "GoalRecommended",
  GoalMasteryGain = "GoalMasteryGain",
  GoalMastered = "GoalMastered",
  FocusSet = "FocusSet",
  PlanSet = "PlanSet",
}
export interface DialogueMessage {
  id?: string;
  text: string;
  type: DialogueMessageType;
  animation: AnimationType;
  audioText?: string;
  audioFile?: string | File;
  deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}
export interface DialogueFeedback {
  positive: DialogueMessage;
  negative: DialogueMessage;
}
export interface DialogueValidation {
  regex: string;
  requiredRegex: string;
  wordCount: number;
  minWordCount: number;
  characterCount: number;
  minCharacterCount: number;
  feedback: DialogueFeedback;
}

/** GOAL */

export enum SubscriberType {
  XAPI_Listener = "XAPI_Listener",
  Mastery_Override = "Mastery_Override",
  LessonRecommend_Override = "LessonRecommend_Override",
}
export interface Subscriber {
  type: SubscriberType;
  url: string;
  accessHeaders: Attribute[];
}
export interface Goal extends DatabaseItem {
  org?: string;
  topics: string[];
  plans: string[];
  learnerTypes: string[];
  survey?: string;
  apiOverrides: Subscriber[];
}

/** INTRO */

export enum IntroSlideType {
  Text = "text",
  Image = "image",
  Video = "video",
}
export interface IntroSlide {
  title: string;
  type: IntroSlideType;
  content: string;
  dialogue: DialogueMessage[];
  actions: Attribute[];
  conditions: Attribute[];
}
export interface Intro extends DatabaseItem {
  org: string;
  slides: IntroSlide[];
}

/** KNOWLEDGE COMPONENT */

export interface Attribute {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}
export interface KnowledgeComponent {
  id: string;
  name: string;
  desc: string;
  deleted: boolean;
}
export interface KnowledgeComponentRelevance {
  kc: string;
  relevance: number;
}

/** LEARNER TYPE */

export interface LearnerType {
  id: string;
  name: string;
  desc: string;
  deleted: boolean;
  characteristics: string[];
}

/** LESSON */

export enum LessonType {
  WEB = "web",
  WEB_ACTIVE = "web-active",
  TEXT_DOC = "text-doc",
  VIDEO = "video",
  OPENTUTOR = "opentutor",
  PLAN_DIALOGUE = "plan-dialogue",
  MULTIPLE_CHOICE = "multiple-choice",
}
export enum UserLessonStatus {
  NONE,
  STARTED,
  COMPLETED,
  DONE,
}
export interface LessonTraitReq {
  type: "match" | "greater";
  match?: string;
  matchValidValues?: number[];
  greaterPreferred?: string[];
  greaterCompared?: string[];
}
export interface Asset {
  name: string;
  type: string;
  uri?: string;
  duration?: number;
  inlineMcq?: MultipleChoiceLesson;
  inlineOpentutor?: OpentutorLesson;
}
export interface Resource {
  id: string;
  uri: string;
  duration: number;
  explorationLevel: number;
  assets: Asset[];
  knowledgeComponents: KnowledgeComponentRelevance[];
  license?: string;
  firstTimeCategory?: string;
  firstTimeParams?: string[];
  inlineMcq?: MultipleChoiceLesson;
  inlineOpentutor?: OpentutorLesson;
}
export interface Lesson extends DatabaseItem {
  org: string;
  type: LessonType;
  downloadable: boolean;
  resources: Resource[];
  randomizeResources?: number;
  remediationScore?: number;
  remedialLessons: string[];
  remedialRequirements?: LessonTraitReq[];
  traitRequirements?: LessonTraitReq[];
  learnerTypes: string[];
}

export interface MultipleChoiceLesson {
  question: MultipleChoiceQuestion;
  answers: MultipleChoiceAnswer[];
  hints: string[];
  relatedLessons: string[];
  isRandomized: boolean;
  scoreMode: string;
}
export interface MultipleChoiceQuestion {
  type: string;
  question: string;
  imagePath?: string;
  imageHeader?: string;
  startTime?: number;
  endTime?: number;
}
export interface MultipleChoiceAnswer {
  answerText: string;
  answerFeedback: string;
  isCorrect: boolean;
}
export interface OpentutorLesson {
  lessonId: string;
}

/** ORGANIZATION */

export interface OrgMember {
  user: string;
  role: UserRole;
}
export interface Organization extends DatabaseItem {
  accessCode: string;
  members: OrgMember[];
  config: AppConfig;
}

/** PLAN */

export enum PlanContactMethod {
  PHONE = "phone",
  MOBILE = "mobile",
  EMAIL = "email",
  WEB = "web",
  FACEBOOK = "facebook",
}
export enum PlanEntryType {
  TEXT = "text",
  TRUE_FALSE = "true_false",
  ADDRESS = "address",
  CONTACT = "contact",
}
export interface PlanContactInfo {
  contact: string;
  method: PlanContactMethod;
}
export interface PlanContact {
  name: string;
  primary: number;
  contactInfo: PlanContactInfo[];
}
export interface PlanEntry {
  type: PlanEntryType;
  placeholder?: string;
  isOptional?: boolean;
  value?: string;
}
export interface PlanField {
  header?: string;
  entries: PlanEntry[];
}
export interface PlanSection {
  name: string;
  lesson: string;
  header: string;
  fields: PlanField[];
}
export interface Plan extends DatabaseItem {
  sections: PlanSection[];
}

export interface PlanDialogue {
  id: string;
  name: string;
  plan: string;
  introduction: string;
  conclusion: string;
  questions: PlanDialogueQuestion[];
}
export interface PlanDialogueQuestion {
  question: string;
  section: number;
  field: number;
  entry: number;
  validation: DialogueValidation;
}

/** STUDY COHORT */

export interface StudyCohort extends DatabaseItem {
  code: string;
  goal: string;
  blacklist: string[];
  teams: StudyCohortTeam[];
  members: StudyCohortMember[];
  membersPerTeam: number;
  memberSlotsRemaining: number;
}
export interface StudyCohortTeam {
  name: string;
  icon: string;
  inviteCode: string;
}
export interface StudyCohortMember {
  user: string;
  role: UserRole;
  teamIndex: number;
}

/** SURVEYS */

export enum SurveyQuestionType {
  MultipleChoice = "MultipleChoice",
  Likert = "Likert",
  OpenResponse = "OpenResponse",
}
export enum SurveyOperator {
  GTE = "GTE",
  LTE = "LTE",
  EQ = "EQ",
}
export interface SurveyChoice {
  text: string;
  deltas: Attribute[];
  event?: Attribute;
  validation?: DialogueValidation;
  value?: string | number | boolean;
}
export interface SurveyCondition {
  name: string;
  operator: SurveyOperator;
  value: number;
}
export interface SurveyQuestion {
  type: SurveyQuestionType;
  question: string;
  preconditions: SurveyCondition[];
  choices: SurveyChoice[];
  dialogue: DialogueMessage[];
  randomizeChoices?: boolean;
  selectMultiple?: boolean;
}
export interface Survey extends DatabaseItem {
  questions: SurveyQuestion[];
  specialEventConditions: SurveyCondition[];
}

/** TOPICS */

export enum TopicRecommender {
  VALUE = "value",
  LINEAR = "linear",
  LINEAR_REQUIRED = "linear_required",
}
export interface Topic extends DatabaseItem {
  org: string;
  recommender: TopicRecommender;
  lessons: string[]; // add to topics
  requiredLessons: string[]; // rename requiredLessonAliases
  prerequisiteTopics: string[];
  knowledgeComponents: KnowledgeComponentRelevance[];
  attributeRelevance: Attribute[];
}

/** USERS */

export enum UserRole {
  USER = "user", // just see content
  AUTHOR = "author", // create + view classes
  CONTENT_MANAGER = "contentManager", // content + cohort management
  ADMIN = "admin", // content + user managment
}
export interface User {
  id: string;
  name: string;
  email: string;
  deleted: boolean;
  userRole: UserRole;
  lastLoginAt: string;
}
export interface UserAccessToken {
  user: User;
  accessToken: string;
}

/** DIALOGUE */

export enum CharacterType {
  pal = "pal",
  sealion = "sealion",
}

export enum ChatMsgType {
  Text = "text",
  Opening = "opening",
  Closing = "closing",
  Question = "question",
  FeedbackPositive = "feedbackPositive",
  FeedbackNegative = "feedbackNegative",
  FeedbackNeutral = "feedbackNeutral",
}
export enum DialogueFeedbackType {
  Text = "text",
  Opening = "opening",
  Closing = "closing",
  Question = "question",
  FeedbackPositive = "feedbackPositive",
  FeedbackNegative = "feedbackNegative",
  FeedbackNeutral = "feedbackNeutral",
}
export enum DialogueSenderType {
  System = "system",
  User = "user",
}
export enum DialogueSessionStatus {
  NONE,
  IN_PROGRESS,
  REVIEW,
  COMPLETED,
}

/** GAIMA */

export const GAIMA_MODELS = [
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4.1-nano",
  "gpt-4o",
  "gpt-4o-mini",
  "gpt-4o-2024-11-20",
  "gpt-4o-2024-08-06",
  "gpt-4o-2024-05-13",
  "gpt-4-turbo",
  "gpt-4-turbo-2024-04-09",
  "gpt-4-0125-preview",
  "gpt-4-turbo-preview",
  "gpt-4-1106-preview",
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-0125",
  "gpt-3.5-turbo-1106",
];
export enum GaimaStatus {
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN_PROGRESS",
  SUCCESS = "SUCCESS",
  FAILED = "FAILURE",
}
export interface GaimaTask {
  id: string;
  status: string;
  statusUrl: string;
  result: GaimaResult;
  error: string;
  lesson: string;
  learnerType: string;
}
export interface GaimaResult {
  originalDocument: string;
  modifiedDocument: string;
  lineChanges: GaimaLineChange[];
}
export interface GaimaLineChange {
  lineNumber: number;
  original: string;
  modified: string;
  variablesDict: Record<string, string>;
}
